/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect } from 'react';
import { Github, Check, X, RefreshCw } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { useToast } from '../../hooks/use-toast';
import { GitHubConfig } from '../../types';

interface ConnectionStatus {
  tested: boolean;
  success: boolean;
  error?: string;
  user?: string;
}

export function GitHubSettings() {
  const { 
    githubConfig, 
    updateGitHubConfig, 
    clearGitHubConfig 
  } = useApp();
  const { toast } = useToast();

  const [formData, setFormData] = useState<GitHubConfig>({
    accessToken: ''
  });

  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({ 
    tested: false, 
    success: false 
  });

  // Load existing config
  useEffect(() => {
    if (githubConfig) {
      setFormData(githubConfig);
    }
  }, [githubConfig]);

  const handleInputChange = (field: keyof GitHubConfig, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setConnectionStatus({ tested: false, success: false });
  };

  const testConnection = async () => {
    if (!formData.accessToken) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: 'Please provide an access token'
      });
      return;
    }

    setIsTestingConnection(true);
    
    try {
      const tempService = new (await import('../../services/githubService')).GitHubService(formData);
      const result = await tempService.testConnection();
      
      if (result.success) {
        setConnectionStatus({ 
          tested: true, 
          success: true,
          user: result.user?.login
        });
      } else {
        setConnectionStatus({
          tested: true,
          success: false,
          error: result.error
        });
      }
    } catch (error: any) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: error.message || 'Connection test failed'
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const saveConfiguration = () => {
    if (connectionStatus.tested && !connectionStatus.success) {
      const confirmed = confirm(
        'The connection test failed, but you can still save this configuration. ' +
        'You may need to verify the settings later. Do you want to continue?'
      );
      if (!confirmed) {
        return;
      }
    }

    updateGitHubConfig(formData);
    toast({
      title: 'Configuration saved',
      description: 'Your GitHub settings have been updated.',
    });
  };

  const clearConfiguration = () => {
    if (confirm('Are you sure you want to clear the GitHub configuration?')) {
      clearGitHubConfig();
      setFormData({
        accessToken: ''
      });
      setConnectionStatus({ tested: false, success: false });
    }
  };

  return (
    <div style={{ 
      marginBottom: '32px',
      padding: '20px',
      background: '#f9f9f9',
      borderRadius: '8px',
      border: '1px solid #ddd'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Github size={20} style={{ marginRight: '8px', color: '#0066cc' }} />
        <h3 style={{ margin: 0 }}>GitHub Integration</h3>
      </div>

      <div className="form-group">
        <label htmlFor="github-token">Personal Access Token</label>
        <input
          id="github-token"
          type="password"
          placeholder="ghp_your_access_token_here"
          value={formData.accessToken}
          onChange={(e) => handleInputChange('accessToken', e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '8px' }}>
          Create a Personal Access Token at{' '}
          <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer">
            https://github.com/settings/tokens
          </a>
          {' '}with <code>repo</code> scope
        </small>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <button
          className="btn"
          onClick={testConnection}
          disabled={isTestingConnection || !formData.accessToken}
          style={{
            width: 'auto',
            padding: '8px 16px',
            fontSize: '14px'
          }}
        >
          {isTestingConnection ? (
            <>
              <RefreshCw size={16} style={{ marginRight: '8px' }} className="spin" />
              Testing Connection...
            </>
          ) : (
            'Test Connection'
          )}
        </button>

        {connectionStatus.tested && (
          <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center' }}>
            {connectionStatus.success ? (
              <>
                <Check size={16} style={{ color: '#28a745', marginRight: '8px' }} />
                <span className="status-badge success">
                  Connected as {connectionStatus.user}
                </span>
              </>
            ) : (
              <>
                <X size={16} style={{ color: '#dc3545', marginRight: '8px' }} />
                <span className="status-badge error">
                  {connectionStatus.error || 'Connection failed'}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      <div style={{ 
        marginTop: '16px',
        padding: '12px',
        background: '#e9ecef',
        borderRadius: '4px',
        fontSize: '13px',
        color: '#495057'
      }}>
        <strong>Note:</strong> Once configured, you can connect repositories to individual products in the Products page.
      </div>

      <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
        <button
          className="btn"
          onClick={saveConfiguration}
          disabled={!formData.accessToken}
          style={{
            padding: '8px 16px',
            fontSize: '14px'
          }}
        >
          Save GitHub Configuration
        </button>
        
        {githubConfig && (
          <button
            className="btn btn-secondary"
            onClick={clearConfiguration}
            style={{
              padding: '8px 16px',
              fontSize: '14px'
            }}
          >
            Clear GitHub Configuration
          </button>
        )}
      </div>
    </div>
  );
}
